// MediCarePlusLayout.js - Modern Medical Store Theme with Service Modal
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  StoreLogo, CartBtn, TabContent, StoreFooter, InlineEditableImage,
  AWS_URL, FaHeart
} from './ThemeLayoutHelpers';

// Custom InlineEditableTextWithNav component - FIXED with p.themeText support
const InlineEditableTextWithNav = ({ p, field, value, className = "", style = {}, children, onNavigate, navPage, isInModal = false }) => {
  const isEditing = p.previewEditMode && p.activeEditField === field;
  const isEditable = typeof p.onActivateField === "function";
  
  const getCurrentValue = () => {
    // Check multiple sources for the value
    if (p.storeTheme && p.storeTheme[field] !== undefined && p.storeTheme[field] !== "") {
      return p.storeTheme[field];
    }
    // Also check p.themeText if available (for preview mode)
    if (p.themeText && p.themeText[field] !== undefined && p.themeText[field] !== "") {
      return p.themeText[field];
    }
    return value;
  };
  
  const [localValue, setLocalValue] = useState(getCurrentValue());

  useEffect(() => {
    if (!isEditing) {
      setLocalValue(getCurrentValue());
    }
  }, [p.storeTheme, field, value, isEditing, p.themeText]);

  const handleActivate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const currentVal = getCurrentValue();
    setLocalValue(currentVal);
    if (typeof p.onActivateField === "function") {
      p.onActivateField(field);
    }
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
  };

  const handleSaveAndClose = () => {
    if (typeof p.onPreviewTextChange === "function") {
      p.onPreviewTextChange(field, localValue);
    }
    if (typeof p.onDeactivateField === "function") {
      p.onDeactivateField();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleSaveAndClose();
      if (!isInModal && onNavigate && navPage) {
        onNavigate(navPage);
      }
    }
  };

  const handleBlur = () => {
    handleSaveAndClose();
  };

  if (isEditing) {
    const inputClass = "w-full rounded-2xl border border-indigo-200 bg-white/90 px-3 py-2 text-sm text-gray-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 dark:border-indigo-700 dark:bg-gray-800 dark:text-gray-100";
    const multiline = field.toLowerCase().includes("subtitle") || field.toLowerCase().includes("description") || field.toLowerCase().includes("overview") || field.toLowerCase().includes("content") || field.toLowerCase().includes("tip");

    return multiline ? (
      <textarea
        autoFocus
        rows={4}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    ) : (
      <input
        autoFocus
        type="text"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    );
  }

  const displayValue = p.storeTheme?.[field] || p.themeText?.[field] || value;
  
  return (
    <span
      className={`${className} ${isEditable ? 'cursor-pointer inline-block' : 'inline-block'}`}
      style={{ whiteSpace: "pre-wrap", ...style }}
      onClick={isEditable ? handleActivate : undefined}
      title={p.previewEditMode ? "Edit text" : undefined}
    >
      {displayValue}
    </span>
  );
};

export default function MediCarePlusLayout({ p }) {
  const T = p.storeTheme || {};
  const primary = T.primaryColor || "#4F46E5"; // Indigo
  const secondary = T.secondaryColor || "#818CF8"; // Light Indigo
  
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [visibleProducts, setVisibleProducts] = useState([]);
  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [animationComplete, setAnimationComplete] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  const [activeSpecialty, setActiveSpecialty] = useState("all");
  
  const heroRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [p.storeTheme]);

  // Hero image fields that should be protected from editing
  const HERO_IMAGE_FIELDS = [
    'themePreviewImage',
    'medplusHeroImage1',
    'medplusHeroImage2',
    'medplusHeroImage3',
    'medplusHeroImage4'
  ];

  // Different medical hero images - DIRECTLY FROM CONFIG
  const medicalHeroImageConfigs = [
    {
      field: "themePreviewImage",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/41e8c22a-646e-4e34-8a29-a1a7ca7df033_medplus1.jpg",
    },
    {
      field: "medplusHeroImage2",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/907e1989-457f-4523-905e-717f70571b76_medplus2.jpg",
    },
    {
      field: "medplusHeroImage3",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/28066bc0-ceb3-4c60-a9ce-67167d92b90b_medplus3.jpg",
    },
    {
      field: "medplusHeroImage4",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/37a9af60-6e3a-403d-9806-d9929e4aa8c5_medplus4.jpg",
    },
  ];

  // Medical Products - Added imageField support
  const medicalProducts = [
    // Prescription Medicines
    {
      id: 1,
      nameKey: "product1_name",
      name: "Amoxicillin 500mg (15 capsules)",
      priceKey: "product1_price",
      price: 299,
      originalPriceKey: "product1_original_price",
      originalPrice: 399,
      imageField: "product1_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/aa61398d-25f8-4d1f-bb97-d9eadd8821c2_Amoxicillin_500mg.jpg",
      categoryKey: "product1_category",
      category: "Prescription",
      subCategory: "Antibiotics",
      descriptionKey: "product1_description",
      description: "Broad-spectrum antibiotic for bacterial infections. Prescription required.",
      badge: "Prescription",
      badgeColor: "#4F46E5",
      quantity: "15 capsules",
      brand: "PharmaTrust",
      type: "Antibiotic",
      dosage: "500mg"
    },
    {
      id: 2,
      nameKey: "product2_name",
      name: "Atorvastatin 20mg (30 tablets)",
      priceKey: "product2_price",
      price: 449,
      originalPriceKey: "product2_original_price",
      originalPrice: 599,
      imageField: "product2_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/7229d504-8bde-4d77-aed7-2943cd2777c1_Atorvastatin_20mg.jpg",
      categoryKey: "product2_category",
      category: "Prescription",
      subCategory: "Cholesterol",
      descriptionKey: "product2_description",
      description: "Statins for cholesterol management. Clinically proven effective.",
      badge: "Popular",
      badgeColor: "#7C3AED",
      quantity: "30 tablets",
      brand: "HeartCare",
      type: "Statin",
      dosage: "20mg"
    },
    {
      id: 3,
      nameKey: "product3_name",
      name: "Omeprazole 20mg (14 capsules)",
      priceKey: "product3_price",
      price: 199,
      originalPriceKey: "product3_original_price",
      originalPrice: 299,
      imageField: "product3_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/1d433d73-5d58-4d49-a361-91c1281983d8_Omeprazole_20mg.jpg",
      categoryKey: "product3_category",
      category: "Prescription",
      subCategory: "Digestive",
      descriptionKey: "product3_description",
      description: "PPI for acid reflux and heartburn relief. Effective and fast-acting.",
      badge: "Essential",
      badgeColor: "#10B981",
      quantity: "14 capsules",
      brand: "DigestWell",
      type: "PPI",
      dosage: "20mg"
    },
    
    // OTC Medicines
    {
      id: 4,
      nameKey: "product4_name",
      name: "Cetirizine 10mg (10 tablets)",
      priceKey: "product4_price",
      price: 89,
      originalPriceKey: "product4_original_price",
      originalPrice: 129,
      imageField: "product4_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/670077ac-a9c9-4316-bc86-d220f68f7f13_Cetirizine_10mg.webp",
      categoryKey: "product4_category",
      category: "OTC",
      subCategory: "Antihistamines",
      descriptionKey: "product4_description",
      description: "Non-drowsy allergy relief for seasonal allergies and hives.",
      badge: "OTC",
      badgeColor: "#4F46E5",
      quantity: "10 tablets",
      brand: "AllerFree",
      type: "Antihistamine",
      dosage: "10mg"
    },
    {
      id: 5,
      nameKey: "product5_name",
      name: "Ibuprofen 400mg (20 tablets)",
      priceKey: "product5_price",
      price: 149,
      originalPriceKey: "product5_original_price",
      originalPrice: 199,
      imageField: "product5_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/b19ad534-558d-4cf0-9864-87794b13351c_Ibuprofen_400mg.png",
      categoryKey: "product5_category",
      category: "OTC",
      subCategory: "Pain Relief",
      descriptionKey: "product5_description",
      description: "NSAID for pain relief and inflammation. Trusted by millions.",
      badge: "Best Seller",
      badgeColor: "#F59E0B",
      quantity: "20 tablets",
      brand: "PainRelief",
      type: "NSAID",
      dosage: "400mg"
    },
    {
      id: 6,
      nameKey: "product6_name",
      name: "Loperamide 2mg (12 capsules)",
      priceKey: "product6_price",
      price: 79,
      originalPriceKey: "product6_original_price",
      originalPrice: 129,
      imageField: "product6_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/752b35e5-748b-4248-950c-60f01299192c_Loperamide_2mg.jpg",
      categoryKey: "product6_category",
      category: "OTC",
      subCategory: "Digestive",
      descriptionKey: "product6_description",
      description: "Fast relief from diarrhea. Easy-to-swallow capsules.",
      badge: "Fast Relief",
      badgeColor: "#10B981",
      quantity: "12 capsules",
      brand: "DigestEase",
      type: "Antidiarrheal",
      dosage: "2mg"
    },
    
    // Supplements
    {
      id: 7,
      nameKey: "product7_name",
      name: "Vitamin D3 2000IU (60 softgels)",
      priceKey: "product7_price",
      price: 399,
      originalPriceKey: "product7_original_price",
      originalPrice: 549,
      imageField: "product7_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/e31cabed-adef-4744-b4d2-763341d50ef1_Vitamin_D3_2000IU.webp",
      categoryKey: "product7_category",
      category: "Supplements",
      subCategory: "Vitamins",
      descriptionKey: "product7_description",
      description: "High-potency Vitamin D3 for immune support and bone health.",
      badge: "Premium",
      badgeColor: "#7C3AED",
      quantity: "60 softgels",
      brand: "NutriMax",
      type: "Vitamin",
      dosage: "2000IU"
    },
    {
      id: 8,
      nameKey: "product8_name",
      name: "Omega-3 Fish Oil (60 capsules)",
      priceKey: "product8_price",
      price: 499,
      originalPriceKey: "product8_original_price",
      originalPrice: 699,
      imageField: "product8_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/91a288a8-5439-41fd-a154-1d598cb7c885_Omega-3_Fish_Oil.jpg",
      categoryKey: "product8_category",
      category: "Supplements",
      subCategory: "Omega-3",
      descriptionKey: "product8_description",
      description: "Pure omega-3 fatty acids for heart and brain health. Mercury-free.",
      badge: "Pure",
      badgeColor: "#4F46E5",
      quantity: "60 capsules",
      brand: "OmegaLife",
      type: "Supplement",
      dosage: "1000mg"
    },
    {
      id: 9,
      nameKey: "product9_name",
      name: "Calcium + Vitamin D (60 tablets)",
      priceKey: "product9_price",
      price: 349,
      originalPriceKey: "product9_original_price",
      originalPrice: 499,
      imageField: "product9_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/5f20f1a3-7921-4b40-a509-a763573a1287_Calcium_+_Vitamin_D.webp",
      categoryKey: "product9_category",
      category: "Supplements",
      subCategory: "Minerals",
      descriptionKey: "product9_description",
      description: "Bone-strengthening formula with calcium and vitamin D3.",
      badge: "Essential",
      badgeColor: "#10B981",
      quantity: "60 tablets",
      brand: "BoneHealth",
      type: "Mineral",
      dosage: "600mg"
    },
    
    // Medical Devices
    {
      id: 10,
      nameKey: "product10_name",
      name: "Blood Glucose Monitor Kit",
      priceKey: "product10_price",
      price: 1299,
      originalPriceKey: "product10_original_price",
      originalPrice: 1799,
      imageField: "product10_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/5662110b-8050-4048-80e0-6bc5b47e67df_Blood_Glucose_Monitor_Kit.jpg",
      categoryKey: "product10_category",
      category: "Devices",
      subCategory: "Diabetes",
      descriptionKey: "product10_description",
      description: "Complete blood glucose monitoring system with test strips and lancets.",
      badge: "Medical Grade",
      badgeColor: "#4F46E5",
      quantity: "Kit",
      brand: "GlucoCheck",
      type: "Monitor",
      warranty: "2 Years"
    },
    {
      id: 11,
      nameKey: "product11_name",
      name: "Digital Stethoscope",
      priceKey: "product11_price",
      price: 2499,
      originalPriceKey: "product11_original_price",
      originalPrice: 3499,
      imageField: "product11_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/4ce422c6-bc22-4be2-a1e7-128a4de3ec50_Digital_Stethoscope.jpg",
      categoryKey: "product11_category",
      category: "Devices",
      subCategory: "Diagnostic",
      descriptionKey: "product11_description",
      description: "Professional digital stethoscope with amplified sound and recording.",
      badge: "Professional",
      badgeColor: "#7C3AED",
      quantity: "1 unit",
      brand: "MediPro",
      type: "Diagnostic",
      warranty: "3 Years"
    },
    {
      id: 12,
      nameKey: "product12_name",
      name: "Portable ECG Monitor",
      priceKey: "product12_price",
      price: 4999,
      originalPriceKey: "product12_original_price",
      originalPrice: 6999,
      imageField: "product12_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/12465050-9880-4159-8dfe-715b20b73650_Portable_ECG_Monitor.jpg",
      categoryKey: "product12_category",
      category: "Devices",
      subCategory: "Cardiac",
      descriptionKey: "product12_description",
      description: "Portable ECG monitor for heart health tracking. Syncs with smartphone.",
      badge: "Smart",
      badgeColor: "#F59E0B",
      quantity: "1 unit",
      brand: "CardioSmart",
      type: "Digital",
      warranty: "2 Years"
    }
  ];

  // Medical Categories - Different layout
  const medicalCategories = [
    { key: "all", icon: "💊", label: "All Products", color: "#4F46E5" },
    { key: "prescription", icon: "📋", label: "Prescription", color: "#7C3AED" },
    { key: "otc", icon: "🏪", label: "OTC Medicines", color: "#10B981" },
    { key: "supplements", icon: "💪", label: "Supplements", color: "#F59E0B" },
    { key: "devices", icon: "🩺", label: "Medical Devices", color: "#EF4444" },
  ];

  // Doctor Specialties
  const doctorSpecialties = [
    { key: "all", label: "All Specialties", icon: "👨‍⚕️" },
    { key: "cardiology", label: "Cardiology", icon: "❤️" },
    { key: "neurology", label: "Neurology", icon: "🧠" },
    { key: "pediatrics", label: "Pediatrics", icon: "👶" },
    { key: "orthopedics", label: "Orthopedics", icon: "🦴" },
    { key: "dermatology", label: "Dermatology", icon: "🧴" },
  ];

  // Featured Doctors - Added imageField
  const featuredDoctors = [
    {
      id: 1,
      nameKey: "doctor1_name",
      name: "Dr. Sarah Johnson",
      specialtyKey: "doctor1_specialty",
      specialty: "Cardiology",
      experienceKey: "doctor1_exp",
      experience: "15 years",
      rating: 4.9,
      reviews: 234,
      imageField: "doctor1_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/85477925-0889-47ed-85ed-cad978208904_default-avatar-icon-of-social-media-user-vector.jpg",
      badge: "Top Doctor",
      badgeColor: "#4F46E5"
    },
    {
      id: 2,
      nameKey: "doctor2_name",
      name: "Dr. Michael Chen",
      specialtyKey: "doctor2_specialty",
      specialty: "Neurology",
      experienceKey: "doctor2_exp",
      experience: "12 years",
      rating: 4.8,
      reviews: 187,
      imageField: "doctor2_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/85477925-0889-47ed-85ed-cad978208904_default-avatar-icon-of-social-media-user-vector.jpg",
      badge: "Expert",
      badgeColor: "#7C3AED"
    },
    {
      id: 3,
      nameKey: "doctor3_name",
      name: "Dr. Emily Rodriguez",
      specialtyKey: "doctor3_specialty",
      specialty: "Pediatrics",
      experienceKey: "doctor3_exp",
      experience: "10 years",
      rating: 5.0,
      reviews: 312,
      imageField: "doctor3_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/85477925-0889-47ed-85ed-cad978208904_default-avatar-icon-of-social-media-user-vector.jpg",
      badge: "Best Pediatrician",
      badgeColor: "#10B981"
    },
    {
      id: 4,
      nameKey: "doctor4_name",
      name: "Dr. James Wilson",
      specialtyKey: "doctor4_specialty",
      specialty: "Orthopedics",
      experienceKey: "doctor4_exp",
      experience: "18 years",
      rating: 4.9,
      reviews: 256,
      imageField: "doctor4_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/85477925-0889-47ed-85ed-cad978208904_default-avatar-icon-of-social-media-user-vector.jpg",
      badge: "Specialist",
      badgeColor: "#F59E0B"
    }
  ];

  // Health Services with detailed tips
  const healthServices = [
    { 
      id: 1, 
      icon: "🏥", 
      title: "Online Consultation", 
      desc: "Video consultations with certified doctors",
      fullDescription: "Connect with experienced doctors from the comfort of your home. Get professional medical advice, prescriptions, and treatment plans through secure video calls.",
      tips: [
        "📱 Download our app for easy scheduling",
        "💻 Have your medical history ready",
        "🩺 Prepare your questions in advance",
        "📋 Follow up with your local pharmacy"
      ]
    },
    { 
      id: 2, 
      icon: "📋", 
      title: "Health Records", 
      desc: "Digital health records and history",
      fullDescription: "Access your complete medical history anytime, anywhere. Our secure digital health records system keeps all your health information organized and easily accessible.",
      tips: [
        "🔒 Your data is encrypted and secure",
        "📊 Track your health metrics over time",
        "📱 Share records with any doctor",
        "🔄 Automatic updates from your visits"
      ]
    },
    { 
      id: 3, 
      icon: "💊", 
      title: "Medicine Delivery", 
      desc: "Free delivery on all prescriptions",
      fullDescription: "Get your prescribed medicines delivered to your doorstep. Our pharmacy partners ensure authentic medications with proper storage and handling.",
      tips: [
        "🚚 Free delivery on orders above ₹500",
        "⏰ Same-day delivery available",
        "📦 Track your order in real-time",
        "💊 All medicines are 100% authentic"
      ]
    },
    { 
      id: 4, 
      icon: "🩺", 
      title: "Health Checkups", 
      desc: "Regular health screening packages",
      fullDescription: "Comprehensive health checkup packages designed for your age and lifestyle. Early detection leads to better health outcomes.",
      tips: [
        "🎯 Get personalized health goals",
        "📊 Complete blood work included",
        "🩺 Consultation with specialists",
        "💡 Lifestyle recommendations"
      ]
    }
  ];

  // Health Articles - Added imageField
  const healthArticles = [
    {
      id: 1,
      titleKey: "article1_title",
      title: "Understanding Hypertension",
      descKey: "article1_desc",
      desc: "Learn about managing blood pressure effectively.",
      imageField: "article1_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/41e8c22a-646e-4e34-8a29-a1a7ca7df033_medplus1.jpg",
      category: "Heart Health",
      author: "Dr. Sarah Johnson",
      date: "Jan 15, 2025"
    },
    {
      id: 2,
      titleKey: "article2_title",
      title: "Managing Diabetes Naturally",
      descKey: "article2_desc",
      desc: "Natural approaches to diabetes management.",
      imageField: "article2_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/37a9af60-6e3a-403d-9806-d9929e4aa8c5_medplus4.jpg",
      category: "Diabetes",
      author: "Dr. Michael Chen",
      date: "Jan 12, 2025"
    },
    {
      id: 3,
      titleKey: "article3_title",
      title: "Mental Health in Modern Times",
      descKey: "article3_desc",
      desc: "Mental health strategies for the modern world.",
      imageField: "article3_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/28066bc0-ceb3-4c60-a9ce-67167d92b90b_medplus3.jpg",
      category: "Mental Health",
      author: "Dr. Emily Rodriguez",
      date: "Jan 10, 2025"
    }
  ];

  // Navigation items
  const navItems = [
    { key: "nav_home", label: "HOME", page: "home" },
    { key: "nav_shop", label: "SHOP", page: "shop" },
    { key: "nav_doctors", label: "DOCTORS", page: "doctors" },
    { key: "nav_services", label: "SERVICES", page: "services" },
    { key: "nav_articles", label: "ARTICLES", page: "articles" },
    { key: "nav_about", label: "ABOUT", page: "about" },
  ];

  // Top bar messages
  const topBarMessages = [
    "🏥 QUALITY HEALTHCARE PRODUCTS",
    "✦ FREE DELIVERY ON ₹500+",
    "💊 100% AUTHENTIC MEDICINES",
    "✦ EXPERT DOCTOR CONSULTATION"
  ];

  // Floating particles
  const particles = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: 2 + Math.random() * 4,
    delay: Math.random() * 4,
    duration: 6 + Math.random() * 6,
    opacity: 0.03 + Math.random() * 0.08,
  }));

  // Auto-rotate hero images - USING CONFIG DIRECTLY
  useEffect(() => {
    if (p.previewEditMode) return;
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % medicalHeroImageConfigs.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [p.previewEditMode]);

  // Animate products
  useEffect(() => {
    const products = p.filteredProducts || [];
    if (products.length === 0) return;
    
    setTimeout(() => setAnimationComplete(true), 300);
    
    products.forEach((_, index) => {
      setTimeout(() => {
        setVisibleProducts(prev => [...prev, index]);
      }, index * 80 + 500);
    });
  }, [p.filteredProducts]);

  // Mouse move for parallax
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 15,
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Scroll handler
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigateTo = useCallback((page, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [p.previewEditMode, p.activeEditField]);

  const handleNavigateAfterEdit = useCallback((page) => {
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const openArticleModal = useCallback((article, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedPost(article);
  }, [p.previewEditMode, p.activeEditField]);

  const closeArticleModal = useCallback(() => {
    setSelectedPost(null);
  }, []);

  const openDoctorModal = useCallback((doctor, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedDoctor(doctor);
  }, [p.previewEditMode, p.activeEditField]);

  const closeDoctorModal = useCallback(() => {
    setSelectedDoctor(null);
  }, []);

  const openServiceModal = useCallback((service, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedService(service);
  }, [p.previewEditMode, p.activeEditField]);

  const closeServiceModal = useCallback(() => {
    setSelectedService(null);
  }, []);

  const handleBackdropClick = useCallback((event) => {
    if (event.target === event.currentTarget) {
      if (p.previewEditMode && p.activeEditField) {
        return;
      }
      setSelectedProduct(null);
      setSelectedPost(null);
      setSelectedDoctor(null);
      setSelectedService(null);
    }
  }, [p.previewEditMode, p.activeEditField]);

  const handleEditHeroImage = useCallback((event, field = null) => {
    event.preventDefault();
    event.stopPropagation();
    if (!p.previewEditMode) {
      return;
    }
    const targetField = field || medicalHeroImageConfigs[activeSlide]?.field || "themePreviewImage";
    const targetIndex = medicalHeroImageConfigs.findIndex((image) => image.field === targetField);
    if (targetIndex >= 0) {
      setActiveSlide(targetIndex);
    }
    if (typeof p.onImageFieldClick === "function") {
      p.onImageFieldClick(targetField, event);
      return;
    }
    if (typeof p.onActivateField === "function") {
      p.onActivateField(targetField);
    }
  }, [activeSlide, medicalHeroImageConfigs, p]);

  const copy = {
    eyebrow: p.storeTheme?.heroEyebrow || "MEDICARE PLUS",
    title: p.storeTheme?.heroTitle || "Your Health, Our Priority",
    subtitle: p.storeTheme?.heroSubtitle || "Trusted healthcare solutions for every family",
    primaryCta: p.storeTheme?.primaryCtaText || "Shop Now",
    secondaryCta: p.storeTheme?.secondaryCtaText || "Consult Doctor",
  };

  // Get editable products with imageField
  const editableMedicalProducts = medicalProducts.map((product) => ({
    ...product,
    image: p.storeTheme?.[product.imageField] || product.image,
  }));

  // Get editable doctors with imageField
  const editableDoctors = featuredDoctors.map((doctor) => ({
    ...doctor,
    image: p.storeTheme?.[doctor.imageField] || doctor.image,
  }));

  // Get editable articles with imageField
  const editableArticles = healthArticles.map((article) => ({
    ...article,
    image: p.storeTheme?.[article.imageField] || article.image,
  }));

  // Get filtered items
  const getFilteredItems = (category) => {
    if (category === "all") return editableMedicalProducts;
    const map = {
      "prescription": "Prescription",
      "otc": "OTC",
      "supplements": "Supplements",
      "devices": "Devices",
    };
    return editableMedicalProducts.filter(item => item.category === map[category]);
  };

  const filteredItems = getFilteredItems(activeCategory);

  // Get filtered doctors
  const getFilteredDoctors = (specialty) => {
    if (specialty === "all") return editableDoctors;
    return editableDoctors.filter(doc => doc.specialty.toLowerCase() === specialty.toLowerCase());
  };

  const filteredDoctors = getFilteredDoctors(activeSpecialty);

  // Get card entrance animation
  const getCardEntrance = (index) => {
    const directions = [
      { x: -50, y: -20, rotate: -3 },
      { x: 50, y: -20, rotate: 3 },
      { x: -50, y: 20, rotate: -2 },
      { x: 50, y: 20, rotate: 2 },
    ];
    return directions[index % directions.length];
  };

  return (
    <div key={refreshKey} className="w-full min-h-screen bg-indigo-50/50 overflow-x-hidden" style={{ fontFamily: T.fontFamily || "'Inter', sans-serif" }}>
      
      {/* Floating Particles */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute medical-float"
            style={{
              left: `${particle.left}%`,
              top: `${particle.top}%`,
              width: `${particle.size * 2}px`,
              height: `${particle.size * 2}px`,
              background: `radial-gradient(circle, ${primary}${Math.floor(15 + Math.random() * 25).toString(16)}, transparent)`,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`,
              opacity: particle.opacity,
            }}
          />
        ))}
      </div>
      
      {/* Top Bar */}
      <div className="relative bg-indigo-900 text-indigo-200 py-2.5 overflow-hidden border-b-2 border-indigo-700/30">
        <div className="flex items-center justify-center gap-6 whitespace-nowrap animate-marquee-slow">
          {topBarMessages.map((msg, idx) => (
            <span key={idx} className="inline-block mx-5 text-xs tracking-wider">
              <InlineEditableTextWithNav 
                p={p} 
                field={`top_bar_message_${idx + 1}`} 
                value={msg}
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </span>
          ))}
        </div>
      </div>
      
      {/* Navigation - Elegant Medical Style */}
      <nav className={`sticky top-0 z-50 transition-all duration-500 ${isScrolled ? "bg-white/95 backdrop-blur-xl shadow-lg py-2 border-b border-indigo-100/20" : "bg-white/90 backdrop-blur-sm py-4"}`}>
        <div className="w-full px-4 md:px-8 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => navigateTo("home", e)}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center text-lg border-2 border-indigo-400/50 shadow-lg shadow-indigo-700/20">
              <span className="text-white font-bold">+</span>
            </div>
            <span className="font-bold text-xl tracking-tight text-indigo-900">
              <InlineEditableTextWithNav 
                p={p} 
                field="businessName" 
                value={p.storeInfo?.businessName || "MediCare+"} 
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </span>
          </div>
          
          <button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!(p.previewEditMode && p.activeEditField)) {
                setMobileMenuOpen(!mobileMenuOpen);
              }
            }} 
            className="md:hidden p-2"
          >
            <svg className="w-6 h-6 text-indigo-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          <div className="hidden md:flex items-center gap-6">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => navigateTo(item.page, e)}
                className={`text-sm font-medium transition-all duration-300 relative group ${
                  activePage === item.page 
                    ? "text-indigo-700" 
                    : "text-indigo-600/70 hover:text-indigo-900"
                }`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={`nav_${item.page}`} 
                  value={item.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={item.page}
                  className="text-inherit"
                />
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-indigo-500 transition-all duration-300 ${activePage === item.page ? "w-full" : "w-0 group-hover:w-full"}`} />
              </button>
            ))}
          </div>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.handleGoToWishlist?.();
                }
              }} 
              className="relative group"
            >
              <div className="p-2 rounded-full transition-all duration-300 hover:bg-indigo-50">
                <FaHeart className="text-lg text-indigo-600/70 hover:text-indigo-900 transition-colors" />
              </div>
              {p.wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-indigo-500 text-white text-[10px] flex items-center justify-center font-bold animate-pulse">
                  {p.wishlistCount}
                </span>
              )}
            </button>
            <CartBtn p={p} dark={false} />
            {p.userMenuElement}
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg py-4 px-4 flex flex-col gap-2 z-50 border-t border-indigo-100">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => {
                  navigateTo(item.page, e);
                  setMobileMenuOpen(false);
                }}
                className={`text-sm py-2 px-3 rounded-lg text-left ${
                  activePage === item.page 
                    ? "bg-indigo-50 text-indigo-700" 
                    : "text-indigo-600/70 hover:text-indigo-900"
                }`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={`nav_${item.page}`} 
                  value={item.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={item.page}
                  className="text-inherit"
                />
              </button>
            ))}
          </div>
        )}
      </nav>
      
      {/* ==================== HOME PAGE ==================== */}
      {activePage === "home" && (
        <>
          {/* Hero Section - FIXED: DIRECTLY USING CONFIG IMAGES */}
          <section className="relative w-full h-screen min-h-[700px] overflow-hidden -mt-[60px]">
            <div className="absolute inset-0 w-full h-full">
              {medicalHeroImageConfigs.map((img, idx) => (
                <div
                  key={img.field}
                  className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out ${
                    idx === activeSlide ? "opacity-100 z-10" : "opacity-0 z-0"
                  }`}
                >
                  <InlineEditableImage p={p} field={img.field} className="h-full w-full">
                    <img 
                      src={p.storeTheme?.[img.field] || img.src} 
                      alt={`Medical ${idx + 1}`} 
                      className="w-full h-full object-cover object-center" 
                      style={{ 
                        transform: `translate(${mousePosition.x * 0.3}px, ${mousePosition.y * 0.3}px) scale(1.05)`, 
                        transition: "transform 0.3s ease-out" 
                      }} 
                    />
                  </InlineEditableImage>
                </div>
              ))}
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/80 via-indigo-900/50 to-transparent z-20 pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-t from-indigo-50/80 via-transparent to-transparent z-20 pointer-events-none" />
              
              {/* Hero edit buttons */}
              {p.previewEditMode && (
                <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-30 flex flex-wrap items-center gap-2 rounded-2xl border border-indigo-300/40 bg-indigo-900/35 px-3 py-2 backdrop-blur-sm">
                  {medicalHeroImageConfigs.map((image, index) => (
                    <button
                      key={image.field}
                      type="button"
                      onClick={(event) => handleEditHeroImage(event, image.field)}
                      className={`rounded-full px-3 py-1 text-xs font-medium transition ${index === activeSlide ? "bg-indigo-500 text-white" : "bg-white/10 text-indigo-100 hover:bg-white/20"}`}
                    >
                      Hero {index + 1}
                    </button>
                  ))}
                </div>
              )}
              
              <div className="absolute bottom-0 left-0 right-0 z-20">
                <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
                  <path d="M0 60 C 360 0, 720 120, 1080 60 C 1260 30, 1380 90, 1440 60 L 1440 120 L 0 120 Z" fill="#F5F3FF" opacity="0.95" />
                </svg>
              </div>
            </div>
            
            <div className="relative z-30 h-full w-full px-4 md:px-8 flex items-center">
              <div className="max-w-3xl">
                <div className="inline-block mb-6 animate-bounce-in">
                  <div className="flex items-center gap-3 px-5 py-2.5 bg-indigo-800/40 backdrop-blur-md rounded-full border border-indigo-400/30">
                    <span className="w-2 h-2 rounded-full bg-indigo-400 animate-ping" />
                    <span className="text-indigo-200 text-xs tracking-[0.2em] uppercase">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="heroEyebrow" 
                        value={copy.eyebrow}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="home"
                      />
                    </span>
                    <span className="w-2 h-2 rounded-full bg-indigo-400 animate-pulse" />
                  </div>
                </div>
                <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[1.05] tracking-tight">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroTitle" 
                    value={copy.title}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  >
                    {copy.title.split(" ").map((word, i) => (
                      <span key={i} className="inline-block animate-word-pop" style={{ animationDelay: `${i * 0.12}s` }}>{word}{" "}</span>
                    ))}
                  </InlineEditableTextWithNav>
                </h1>
                <p className="mt-6 text-indigo-100/90 text-lg max-w-xl animate-slide-right">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroSubtitle" 
                    value={copy.subtitle}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <button 
                    onClick={(e) => navigateTo("shop", e)} 
                    className="group px-8 py-4 rounded-xl bg-indigo-600 text-white font-semibold tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-indigo-600/30 flex items-center gap-2 animate-slide-up"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="primaryCtaText" 
                      value={copy.primaryCta}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="shop"
                    />
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </button>
                  <button 
                    onClick={(e) => navigateTo("doctors", e)} 
                    className="px-8 py-4 rounded-xl border-2 border-indigo-400/50 text-indigo-200 font-semibold tracking-wide transition-all duration-300 hover:bg-indigo-600/30 hover:border-indigo-300 backdrop-blur-sm animate-slide-up"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="secondaryCtaText" 
                      value={copy.secondaryCta}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="doctors"
                    />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="absolute bottom-16 left-1/2 -translate-x-1/2 animate-bounce-slow z-30">
              <div className="flex flex-col items-center gap-1">
                <span className="text-indigo-200/60 text-[10px] tracking-wider">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="scroll_text" 
                    value="SCROLL TO EXPLORE"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </span>
                <div className="w-5 h-8 border-2 border-indigo-400/30 rounded-full flex justify-center">
                  <div className="w-1 h-2 bg-indigo-400/60 rounded-full mt-1 animate-scroll-dot" />
                </div>
              </div>
            </div>
          </section>
          
          {/* Featured Badge */}
          <div className="relative z-10 -mt-6 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-xl shadow-lg border border-indigo-200 mt-6">
              <span className="text-indigo-600 text-sm">🏥</span>
              <span className="text-indigo-900 text-xs font-semibold tracking-wider">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="collection_badge" 
                  value="TRUSTED BY 10,000+ PATIENTS"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </span>
              <span className="text-indigo-600 text-sm">🏥</span>
            </div>
          </div>
          
          {/* Health Services - Quick Access with Click to Open Modal */}
          <section className="w-full px-4 md:px-8 py-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {healthServices.map((service, idx) => (
                <div 
                  key={service.id} 
                  className="group cursor-pointer animate-card-fly" 
                  style={{ animationDelay: `${idx * 0.08}s` }}
                  onClick={(e) => openServiceModal(service, e)}
                >
                  <div className="relative overflow-hidden rounded-xl p-5 text-center transition-all duration-500 hover:-translate-y-2 hover:shadow-xl bg-white border border-indigo-200 hover:border-indigo-400">
                    <div className="text-4xl mb-3 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6">
                      {service.icon}
                    </div>
                    <h3 className="font-semibold text-indigo-800 text-sm">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={`service_${service.id}_title`} 
                        value={service.title}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="services"
                      />
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={`service_${service.id}_desc`} 
                        value={service.desc}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="services"
                      />
                    </p>
                    <div className="mt-2 text-indigo-500 text-xs font-medium group-hover:gap-2 transition-all flex items-center justify-center gap-1">
                      Learn More <span className="group-hover:translate-x-1 transition-transform">→</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Category Grid - Modern Style */}
          <section className="w-full px-4 md:px-8 py-8 bg-white">
            <div className="text-center mb-8">
              <span className="text-indigo-600 text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="categories_badge" 
                  value="Shop Categories"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="shop"
                />
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-indigo-900 mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="categories_title" 
                  value="Browse by Category"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="shop"
                />
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {medicalCategories.map((cat, idx) => (
                <div 
                  key={cat.key} 
                  className="group cursor-pointer animate-card-fly" 
                  style={{ animationDelay: `${idx * 0.08}s` }}
                  onClick={(e) => {
                    if (cat.key === "all") {
                      navigateTo("shop", e);
                    } else {
                      setActiveCategory(cat.key);
                      navigateTo("shop", e);
                    }
                  }}
                >
                  <div className="relative overflow-hidden rounded-xl p-5 text-center transition-all duration-500 hover:-translate-y-2 hover:shadow-xl bg-indigo-50 border border-indigo-200 hover:border-indigo-400">
                    <div className="text-3xl mb-2 transition-all duration-300 group-hover:scale-110">
                      {cat.icon}
                    </div>
                    <h3 className="font-semibold text-indigo-800 text-xs">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={`cat_${cat.key}`} 
                        value={cat.label}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="shop"
                      />
                    </h3>
                    <div className="mt-1 h-0.5 w-0 group-hover:w-full transition-all duration-500 mx-auto" style={{ backgroundColor: cat.color }} />
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Featured Products */}
          <section className="w-full px-4 md:px-8 py-8">
            <div className="text-center mb-10">
              <span className="text-indigo-600 text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="featured_badge" 
                  value="Best Sellers"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="shop"
                />
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-indigo-900 mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="featured_title" 
                  value="Most Popular Products"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="shop"
                />
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-indigo-600 to-indigo-400 mx-auto mt-4 rounded-full" />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {editableMedicalProducts.slice(0, 4).map((product, idx) => {
                const entrance = getCardEntrance(idx);
                const isVisible = animationComplete;
                return (
                  <div 
                    key={product.id} 
                    className={`group cursor-pointer transition-all duration-700 ${isVisible ? "opacity-100 translate-x-0 translate-y-0 rotate-0" : `opacity-0 translate-x-[${entrance.x}px] translate-y-[${entrance.y}px] rotate-[${entrance.rotate}deg]`}`}
                    onMouseEnter={() => setHoveredCard(idx)}
                    onMouseLeave={() => setHoveredCard(null)}
                    onClick={() => setSelectedProduct(product)}
                  >
                    <div className="relative overflow-hidden rounded-xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-indigo-200 hover:border-indigo-400">
                      <div className="relative h-48 overflow-hidden bg-indigo-50">
                        <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                          <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                        </InlineEditableImage>
                        {product.badge && (
                          <div className="absolute top-2 left-2 px-3 py-1 rounded-lg text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                            {product.badge}
                          </div>
                        )}
                        <div className="absolute top-2 right-2 bg-indigo-700/80 text-indigo-100 text-[10px] font-bold px-3 py-1 rounded-lg">
                          {product.quantity}
                        </div>
                      </div>
                      <div className="p-3">
                        <div className="flex items-center gap-1 mb-1">
                          <span className="text-xs text-gray-500">{product.brand}</span>
                          <span className="text-xs text-gray-300">•</span>
                          <span className="text-xs text-gray-500">{product.category}</span>
                        </div>
                        <h3 className="font-semibold text-indigo-900 text-sm line-clamp-1">
                          <InlineEditableTextWithNav 
                            p={p} 
                            field={product.nameKey} 
                            value={product.name}
                            onNavigate={handleNavigateAfterEdit}
                            navPage="home"
                          />
                        </h3>
                        <div className="mt-2 flex items-center justify-between">
                          <div>
                            <span className="text-lg font-bold text-indigo-700">₹<InlineEditableTextWithNav 
                              p={p} 
                              field={product.priceKey} 
                              value={product.price.toString()}
                              onNavigate={handleNavigateAfterEdit}
                              navPage="home"
                            /></span>
                            {product.originalPrice > product.price && (
                              <span className="text-xs text-gray-400 line-through ml-1">₹{product.originalPrice}</span>
                            )}
                          </div>
                          <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-lg border border-indigo-200">
                            {product.subCategory}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* View All Products Button - FIXED: Properly redirects to shop page */}
            {/* <div className="text-center mt-10">
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!(p.previewEditMode && p.activeEditField)) {
                    setActiveCategory("all"); // Reset to show all products
                    setActivePage("shop");
                    setMobileMenuOpen(false);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                }} 
                className="px-8 py-3 rounded-xl bg-indigo-700 text-white font-semibold hover:bg-indigo-800 transition-all duration-300 hover:scale-105 shadow-lg shadow-indigo-700/20"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="view_all_button" 
                  value="View All Products"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="shop"
                />
              </button>
            </div> */}
          </section>
          
          {/* Featured Doctors */}
          <section className="w-full px-4 md:px-8 py-8 bg-indigo-50/50">
            <div className="text-center mb-10">
              <span className="text-indigo-600 text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="doctors_badge" 
                  value="Our Experts"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="doctors"
                />
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-indigo-900 mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="doctors_title" 
                  value="Meet Our Specialists"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="doctors"
                />
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-indigo-600 to-indigo-400 mx-auto mt-4 rounded-full" />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {editableDoctors.map((doctor, idx) => (
                <div 
                  key={doctor.id} 
                  className="group cursor-pointer animate-card-fly" 
                  style={{ animationDelay: `${idx * 0.08}s` }}
                  onClick={() => openDoctorModal(doctor)}
                >
                  <div className="relative overflow-hidden rounded-xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-indigo-200 hover:border-indigo-400">
                    <div className="relative h-48 overflow-hidden bg-indigo-50">
                      <InlineEditableImage p={p} field={doctor.imageField} className="block h-full w-full overflow-hidden">
                        <img src={doctor.image} alt={doctor.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                      </InlineEditableImage>
                      {doctor.badge && (
                        <div className="absolute top-2 left-2 px-3 py-1 rounded-lg text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: doctor.badgeColor }}>
                          {doctor.badge}
                        </div>
                      )}
                      <div className="absolute bottom-2 right-2 bg-indigo-700/80 text-indigo-100 text-[10px] font-bold px-3 py-1 rounded-lg">
                        ★ {doctor.rating}
                      </div>
                    </div>
                    <div className="p-3">
                      <h3 className="font-semibold text-indigo-900 text-sm">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={doctor.nameKey} 
                          value={doctor.name}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="doctors"
                        />
                      </h3>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-indigo-600 font-medium">
                          <InlineEditableTextWithNav 
                            p={p} 
                            field={doctor.specialtyKey} 
                            value={doctor.specialty}
                            onNavigate={handleNavigateAfterEdit}
                            navPage="doctors"
                          />
                        </span>
                        <span className="text-xs text-gray-400">
                          <InlineEditableTextWithNav 
                            p={p} 
                            field={doctor.experienceKey} 
                            value={doctor.experience}
                            onNavigate={handleNavigateAfterEdit}
                            navPage="doctors"
                          />
                        </span>
                      </div>
                      <div className="mt-2 flex items-center justify-between">
                        <span className="text-xs text-gray-500">{doctor.reviews} reviews</span>
                        <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-lg">Book Now</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Health Articles */}
          <section className="w-full px-4 md:px-8 py-8">
            <div className="text-center mb-10">
              <span className="text-indigo-600 text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="articles_badge" 
                  value="Health Articles"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="articles"
                />
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-indigo-900 mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="articles_title" 
                  value="Latest Health Insights"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="articles"
                />
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-indigo-600 to-indigo-400 mx-auto mt-4 rounded-full" />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {editableArticles.map((article) => (
                <div 
                  key={article.id} 
                  className="group cursor-pointer rounded-xl overflow-hidden bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-indigo-200 hover:border-indigo-400 animate-card-fly"
                  onClick={() => openArticleModal(article)}
                >
                  <div className="h-40 overflow-hidden">
                    <InlineEditableImage p={p} field={article.imageField} className="block h-full w-full overflow-hidden">
                      <img src={article.image} alt={article.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    </InlineEditableImage>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-indigo-600 font-semibold">{article.category}</span>
                      <span className="text-xs text-gray-400">•</span>
                      <span className="text-xs text-gray-400">{article.date}</span>
                    </div>
                    <h4 className="font-semibold text-indigo-900 text-sm">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={article.titleKey} 
                        value={article.title}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="articles"
                      />
                    </h4>
                    <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={article.descKey} 
                        value={article.desc}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="articles"
                      />
                    </p>
                    <div className="mt-3 text-indigo-700 text-xs font-semibold group-hover:gap-2 transition-all flex items-center gap-1">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="read_more_button" 
                        value="Read More"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="articles"
                      />
                      <span className="group-hover:translate-x-1 transition-transform">→</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
      
      {/* ==================== SHOP PAGE ==================== */}
      {activePage === "shop" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-indigo-600 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="shop_badge" 
                value="Our Store"
                onNavigate={handleNavigateAfterEdit}
                navPage="shop"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-indigo-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="shop_title" 
                value="Healthcare Products"
                onNavigate={handleNavigateAfterEdit}
                navPage="shop"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="shop_subtitle" 
                value="Quality products for your health and wellness"
                onNavigate={handleNavigateAfterEdit}
                navPage="shop"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-indigo-600 to-indigo-400 mx-auto mt-4 rounded-full" />
          </div>
          
          {/* Shop Categories Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {medicalCategories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                  activeCategory === cat.key 
                    ? "bg-indigo-600 text-white shadow-lg" 
                    : "bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                }`}
              >
                {cat.icon} {cat.label}
              </button>
            ))}
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {getFilteredItems(activeCategory).map((product, idx) => (
              <div 
                key={product.id} 
                className="group cursor-pointer bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-indigo-200 hover:border-indigo-400 animate-card-fly"
                style={{ animationDelay: `${idx * 0.06}s` }}
                onClick={() => setSelectedProduct(product)}
              >
                <div className="relative h-48 overflow-hidden bg-indigo-50">
                  <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                  {product.badge && (
                    <div className="absolute top-2 left-2 px-3 py-1 rounded-lg text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                      {product.badge}
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-indigo-700/80 text-indigo-100 text-[10px] font-bold px-3 py-1 rounded-lg">
                    {product.quantity}
                  </div>
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-xs text-gray-500">{product.brand}</span>
                    <span className="text-xs text-gray-300">•</span>
                    <span className="text-xs text-gray-500">{product.category}</span>
                  </div>
                  <h3 className="font-semibold text-indigo-900 text-sm">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={product.nameKey} 
                      value={product.name}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="shop"
                    />
                  </h3>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-lg font-bold text-indigo-700">₹<InlineEditableTextWithNav 
                      p={p} 
                      field={product.priceKey} 
                      value={product.price.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="shop"
                    /></span>
                    {product.originalPrice > product.price && (
                      <span className="text-xs text-gray-400 line-through">₹{product.originalPrice}</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    {product.type} • {product.dosage || product.warranty}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== DOCTORS PAGE ==================== */}
      {activePage === "doctors" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-indigo-600 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="doctors_page_badge" 
                value="Our Team"
                onNavigate={handleNavigateAfterEdit}
                navPage="doctors"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-indigo-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="doctors_page_title" 
                value="Expert Doctors"
                onNavigate={handleNavigateAfterEdit}
                navPage="doctors"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="doctors_page_subtitle" 
                value="Meet our team of qualified specialists"
                onNavigate={handleNavigateAfterEdit}
                navPage="doctors"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-indigo-600 to-indigo-400 mx-auto mt-4 rounded-full" />
          </div>
          
          {/* Doctor Specialties Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {doctorSpecialties.map((spec) => (
              <button
                key={spec.key}
                onClick={() => setActiveSpecialty(spec.key)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                  activeSpecialty === spec.key 
                    ? "bg-indigo-600 text-white shadow-lg" 
                    : "bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                }`}
              >
                {spec.icon} {spec.label}
              </button>
            ))}
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredDoctors.map((doctor) => (
              <div 
                key={doctor.id} 
                className="group cursor-pointer bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-indigo-200 hover:border-indigo-400"
                onClick={() => openDoctorModal(doctor)}
              >
                <div className="relative h-56 overflow-hidden bg-indigo-50">
                  <InlineEditableImage p={p} field={doctor.imageField} className="block h-full w-full overflow-hidden">
                    <img src={doctor.image} alt={doctor.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                  {doctor.badge && (
                    <div className="absolute top-2 left-2 px-3 py-1 rounded-lg text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: doctor.badgeColor }}>
                      {doctor.badge}
                    </div>
                  )}
                  <div className="absolute bottom-2 right-2 bg-indigo-700/80 text-indigo-100 text-[10px] font-bold px-3 py-1 rounded-lg">
                    ★ {doctor.rating}
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-indigo-900">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={doctor.nameKey} 
                      value={doctor.name}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="doctors"
                    />
                  </h3>
                  <p className="text-sm text-indigo-600 font-medium">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={doctor.specialtyKey} 
                      value={doctor.specialty}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="doctors"
                    />
                  </p>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={doctor.experienceKey} 
                        value={doctor.experience}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="doctors"
                      />
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== SERVICES PAGE ==================== */}
      {activePage === "services" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-indigo-600 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="services_page_badge" 
                value="Our Services"
                onNavigate={handleNavigateAfterEdit}
                navPage="services"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-indigo-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="services_page_title" 
                value="Healthcare Services"
                onNavigate={handleNavigateAfterEdit}
                navPage="services"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="services_page_subtitle" 
                value="Comprehensive healthcare solutions for you"
                onNavigate={handleNavigateAfterEdit}
                navPage="services"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-indigo-600 to-indigo-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {healthServices.map((service) => (
              <div 
                key={service.id} 
                className="group bg-white rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-indigo-200 hover:border-indigo-400 cursor-pointer"
                onClick={() => openServiceModal(service)}
              >
                <div className="flex items-start gap-4">
                  <div className="text-5xl transition-all duration-300 group-hover:scale-110 group-hover:rotate-6">
                    {service.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-indigo-900">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={`service_${service.id}_title`} 
                        value={service.title}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="services"
                      />
                    </h3>
                    <p className="text-gray-600 mt-2">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={`service_${service.id}_desc`} 
                        value={service.desc}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="services"
                      />
                    </p>
                    <div className="mt-3 text-indigo-600 font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                      Click for Details <span>→</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== ARTICLES PAGE ==================== */}
      {activePage === "articles" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-indigo-600 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="articles_page_badge" 
                value="Articles"
                onNavigate={handleNavigateAfterEdit}
                navPage="articles"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-indigo-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="articles_page_title" 
                value="Health Articles"
                onNavigate={handleNavigateAfterEdit}
                navPage="articles"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="articles_page_subtitle" 
                value="Expert health advice and insights"
                onNavigate={handleNavigateAfterEdit}
                navPage="articles"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-indigo-600 to-indigo-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {editableArticles.map((article) => (
              <div 
                key={article.id} 
                className="group bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer border border-indigo-200 hover:border-indigo-400"
                onClick={() => openArticleModal(article)}
              >
                <div className="h-48 overflow-hidden">
                  <InlineEditableImage p={p} field={article.imageField} className="block h-full w-full overflow-hidden">
                    <img src={article.image} alt={article.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-lg font-semibold">
                      {article.category}
                    </span>
                    <span className="text-xs text-gray-400">{article.date}</span>
                  </div>
                  <h3 className="text-lg font-bold text-indigo-900 mb-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={article.titleKey} 
                      value={article.title}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="articles"
                    />
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={article.descKey} 
                      value={article.desc}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="articles"
                    />
                  </p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-gray-400">By {article.author}</span>
                    <span className="text-indigo-600 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                      Read <span>→</span>
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== ABOUT PAGE ==================== */}
      {activePage === "about" && (
        <section className="w-full px-4 md:px-8 py-12 md:py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-indigo-600 text-sm tracking-wider uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_badge" 
                  value="About MediCare+"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-indigo-900 mt-3">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_title" 
                  value="Your Trusted Healthcare Partner"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </h2>
              <div className="w-16 h-1 bg-indigo-600 mt-4 mb-6" />
              <p className="text-gray-600 leading-relaxed mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para1" 
                  value="MediCare+ is a premier healthcare provider offering comprehensive medical products and services. We are committed to excellence in healthcare delivery."
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para2" 
                  value="Our team of dedicated professionals works tirelessly to ensure you receive the best healthcare products and services. We believe in making healthcare accessible to everyone."
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </p>
              <div className="flex flex-wrap gap-6">
                <div>
                  <p className="text-3xl font-bold text-indigo-700">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat1_value" 
                      value="500+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat1_label" 
                      value="Products"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-indigo-700">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat2_value" 
                      value="50+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat2_label" 
                      value="Expert Doctors"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-indigo-700">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat3_value" 
                      value="10k+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat3_label" 
                      value="Happy Patients"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden shadow-2xl border-4 border-indigo-200/50">
              <InlineEditableImage p={p} field="medplusAboutImage" className="block h-full w-full overflow-hidden">
                <img 
                  src={p.storeTheme?.medplusAboutImage || "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/28066bc0-ceb3-4c60-a9ce-67167d92b90b_medplus3.jpg"} 
                  alt="About MediCare+" 
                  className="w-full h-full object-cover" 
                />
              </InlineEditableImage>
            </div>
          </div>
        </section>
      )}
      
      {/* ==================== PRODUCT TABS ==================== */}
      <section className="w-full px-4 md:px-8 mt-8">
        <div className="flex justify-center gap-2 flex-wrap">
          {[
            ["products", "💊 Shop"],
            ["services", "🏥 Services"],
            ["details", "📖 About"]
          ].map(([key, label]) => (
            <button 
              key={key} 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.setActiveTab(key);
                }
              }} 
              className={`px-5 md:px-7 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 ${p.activeTab === key ? "bg-indigo-600 text-white shadow-lg" : "text-gray-500 hover:text-indigo-700"}`}
            >
              <InlineEditableTextWithNav 
                p={p} 
                field={`tab_${key}_label`} 
                value={label}
                onNavigate={handleNavigateAfterEdit}
                navPage={activePage}
              />
            </button>
          ))}
        </div>
      </section>
      
      {/* Main Content */}
      <section className="flex-1 w-full px-4 md:px-8 py-8 md:py-12">
        <TabContent p={p} />
      </section>
      
      <StoreFooter p={p} />
      
      {/* ==================== MODALS ==================== */}
      
      {/* Product Modal */}
      {selectedProduct && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-md w-full bg-white rounded-xl overflow-hidden shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-64">
              <InlineEditableImage p={p} field={selectedProduct.imageField} className="block h-full w-full overflow-hidden">
                <img src={selectedProduct.image} alt={selectedProduct.name} className="w-full h-full object-cover" />
              </InlineEditableImage>
              <button 
                onClick={() => setSelectedProduct(null)} 
                className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/50 text-white hover:bg-indigo-600 transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
              {selectedProduct.badge && (
                <div className="absolute top-3 left-3 px-3 py-1 rounded-lg text-white text-xs font-bold shadow-lg" style={{ backgroundColor: selectedProduct.badgeColor }}>
                  {selectedProduct.badge}
                </div>
              )}
              <div className="absolute bottom-3 right-3 bg-indigo-700/80 text-indigo-100 text-xs font-bold px-3 py-1 rounded-lg">
                {selectedProduct.quantity}
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-gray-500">{selectedProduct.brand}</span>
                <span className="text-xs text-gray-300">•</span>
                <span className="text-xs text-gray-500">{selectedProduct.category}</span>
                <span className="text-xs text-gray-300">•</span>
                <span className="text-xs text-gray-500">{selectedProduct.subCategory}</span>
              </div>
              <h3 className="text-2xl font-bold text-indigo-900">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedProduct.nameKey} 
                  value={selectedProduct.name}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </h3>
              <p className="text-gray-600 text-sm mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedProduct.descriptionKey} 
                  value={selectedProduct.description}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded-lg border border-indigo-200">
                  {selectedProduct.subCategory}
                </span>
                {selectedProduct.dosage && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-lg">
                    💊 {selectedProduct.dosage}
                  </span>
                )}
                {selectedProduct.type && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-lg">
                    {selectedProduct.type}
                  </span>
                )}
              </div>
              <div className="mt-4 flex items-center gap-3">
                <span className="text-3xl font-bold text-indigo-700">
                  ₹<InlineEditableTextWithNav 
                    p={p} 
                    field={selectedProduct.priceKey} 
                    value={selectedProduct.price.toString()}
                    onNavigate={handleNavigateAfterEdit}
                    navPage={activePage}
                    isInModal={true}
                  />
                </span>
                {selectedProduct.originalPrice > selectedProduct.price && (
                  <span className="text-sm text-gray-400 line-through">
                    ₹<InlineEditableTextWithNav 
                      p={p} 
                      field={selectedProduct.originalPriceKey} 
                      value={selectedProduct.originalPrice.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage={activePage}
                      isInModal={true}
                    />
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Doctor Modal */}
      {selectedDoctor && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-md w-full bg-white rounded-xl overflow-hidden shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-64">
              <InlineEditableImage p={p} field={selectedDoctor.imageField} className="block h-full w-full overflow-hidden">
                <img src={selectedDoctor.image} alt={selectedDoctor.name} className="w-full h-full object-cover" />
              </InlineEditableImage>
              <button 
                onClick={closeDoctorModal} 
                className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/50 text-white hover:bg-indigo-600 transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
              {selectedDoctor.badge && (
                <div className="absolute top-3 left-3 px-3 py-1 rounded-lg text-white text-xs font-bold shadow-lg" style={{ backgroundColor: selectedDoctor.badgeColor }}>
                  {selectedDoctor.badge}
                </div>
              )}
              <div className="absolute bottom-3 right-3 bg-indigo-700/80 text-indigo-100 text-xs font-bold px-3 py-1 rounded-lg">
                ★ {selectedDoctor.rating}
              </div>
            </div>
            <div className="p-6">
              <h3 className="text-2xl font-bold text-indigo-900">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedDoctor.nameKey} 
                  value={selectedDoctor.name}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="doctors"
                  isInModal={true}
                />
              </h3>
              <p className="text-indigo-600 font-medium">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedDoctor.specialtyKey} 
                  value={selectedDoctor.specialty}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="doctors"
                  isInModal={true}
                />
              </p>
              <div className="mt-4 flex items-center gap-4">
                <span className="text-sm text-gray-500">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedDoctor.experienceKey} 
                    value={selectedDoctor.experience}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="doctors"
                    isInModal={true}
                  />
                </span>
                <span className="text-sm text-gray-500">{selectedDoctor.reviews} reviews</span>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Article Modal */}
      {selectedPost && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-2xl w-full bg-white rounded-xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 bg-white border-b border-indigo-100 p-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-indigo-900">Health Article</h3>
              <button 
                onClick={closeArticleModal} 
                className="w-10 h-10 rounded-full bg-gray-100 text-gray-600 hover:bg-indigo-600 hover:text-white transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <InlineEditableImage p={p} field={selectedPost.imageField} className="block w-full overflow-hidden rounded-xl">
                  <img src={selectedPost.image} alt={selectedPost.title} className="w-full h-64 object-cover" />
                </InlineEditableImage>
              </div>
              <div className="mb-3">
                <span className="inline-block px-3 py-1 bg-indigo-50 text-indigo-700 text-xs rounded-lg font-semibold border border-indigo-200">
                  {selectedPost.category}
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-indigo-900 mb-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedPost.titleKey} 
                  value={selectedPost.title}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="articles"
                  isInModal={true}
                />
              </h2>
              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 border-b border-indigo-100 pb-4 mb-4">
                <span>
                  {selectedPost.date}
                </span>
                <span>•</span>
                <span>
                  By {selectedPost.author}
                </span>
              </div>
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.descKey} 
                    value={selectedPost.desc}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="articles"
                    isInModal={true}
                  />
                </p>
              </div>
              <div className="mt-8 pt-4 border-t border-indigo-100 flex justify-end">
                <button 
                  onClick={closeArticleModal} 
                  className="px-6 py-2.5 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 transition"
                >
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="close_button" 
                    value="Close"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="articles"
                    isInModal={true}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==================== SERVICE MODAL ==================== */}
      {selectedService && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-2xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto animate-slide-up" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="sticky top-0 z-10 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white p-5 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <span className="text-3xl">{selectedService.icon}</span>
                <div>
                  <h3 className="text-xl font-bold">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={`service_${selectedService.id}_title`} 
                      value={selectedService.title}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="services"
                      isInModal={true}
                    />
                  </h3>
                  <p className="text-indigo-200 text-sm">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={`service_${selectedService.id}_desc`} 
                      value={selectedService.desc}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="services"
                      isInModal={true}
                    />
                  </p>
                </div>
              </div>
              <button 
                onClick={closeServiceModal} 
                className="w-10 h-10 rounded-full bg-white/20 text-white hover:bg-white/30 transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
            </div>
            
            {/* Body */}
            <div className="p-6">
              {/* Full Description */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-indigo-600 uppercase tracking-wider mb-2">About This Service</h4>
                <p className="text-gray-700 leading-relaxed">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={`service_${selectedService.id}_full_description`} 
                    value={selectedService.fullDescription}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="services"
                    isInModal={true}
                  />
                </p>
              </div>

              {/* Tips Section */}
              <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-200">
                <h4 className="text-sm font-semibold text-indigo-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <span>💡</span> Helpful Tips
                </h4>
                <ul className="space-y-2">
                  {selectedService.tips.map((tip, index) => (
                    <li key={index} className="flex items-start gap-3 text-gray-700 text-sm">
                      <span className="text-indigo-500 mt-0.5">▸</span>
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={`service_${selectedService.id}_tip_${index + 1}`} 
                        value={tip}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="services"
                        isInModal={true}
                      />
                    </li>
                  ))}
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="mt-6 flex flex-wrap gap-3">
                <button 
                  onClick={closeServiceModal} 
                  className="px-6 py-3 rounded-xl border border-indigo-200 text-indigo-600 font-semibold hover:bg-indigo-50 transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        @keyframes marquee-slow { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }
        .animate-marquee-slow { animation: marquee-slow 25s linear infinite; }
        
        @keyframes medical-float { 
          0% { transform: translate(0, 0) scale(1); opacity: 0.03; }
          50% { transform: translate(15px, -25px) scale(1.5); opacity: 0.08; }
          100% { transform: translate(0, -50px) scale(1); opacity: 0.03; }
        }
        .medical-float { animation: medical-float 6s ease-in-out infinite; border-radius: 50%; }
        
        @keyframes word-pop { 0% { opacity: 0; transform: scale(0.5) rotate(-8deg); } 60% { transform: scale(1.08) rotate(2deg); } 100% { opacity: 1; transform: scale(1) rotate(0); } }
        .animate-word-pop { animation: word-pop 0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes slide-right { from { opacity: 0; transform: translateX(-40px); } to { opacity: 1; transform: translateX(0); } }
        .animate-slide-right { animation: slide-right 0.7s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        
        @keyframes bounce-in { 0% { opacity: 0; transform: scale(0.3); } 50% { transform: scale(1.05); } 70% { transform: scale(0.95); } 100% { opacity: 1; transform: scale(1); } }
        .animate-bounce-in { animation: bounce-in 0.7s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes card-fly { 0% { opacity: 0; transform: translateY(40px) scale(0.9) rotate(-2deg); } 60% { transform: translateY(-5px) scale(1.02) rotate(1deg); } 100% { opacity: 1; transform: translateY(0) scale(1) rotate(0); } }
        .animate-card-fly { animation: card-fly 0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes scroll-dot { 0% { transform: translateY(0); opacity: 1; } 100% { transform: translateY(12px); opacity: 0; } }
        .animate-scroll-dot { animation: scroll-dot 1.5s ease-in-out infinite; }
        
        @keyframes slide-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-slide-up { animation: slide-up 0.5s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
        
        @keyframes bounce { 0%,100% { transform: translateX(-50%) translateY(0); } 50% { transform: translateX(-50%) translateY(-8px); } }
        .animate-bounce-slow { animation: bounce 2s ease-in-out infinite; }
        
        @keyframes slide-up-modal { from { opacity: 0; transform: translateY(30px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .animate-slide-up { animation: slide-up-modal 0.4s ease-out forwards; }
        
        .line-clamp-1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
    </div>
  );
}